import { useRouter } from 'next/router';
import useQuestionDirectionFlow from '@scenes/HomeHub/hooks/hook.animationFlowController';
import { FLOW_ANIMATION_DIRECTION, SURVEY_TYPES, SURVEY_TYPES_SANS_USER } from '@scenes/HomeHub/homehub.constants';
import { useHomeHubStoreActions } from '@scenes/HomeHub/store/client/homehub.store';
import { TSurveyType } from '@scenes/HomeHub/homehub.types';

/**
 * routeController
 *
 * We need to understand the pathing with questions and in user flow.
 * We need to be able to inject either:
 *   user flow: step
 *   survey flow: question
 *
 *   within the url.
 *
 *   Note: This consumes the animationFlowController hook.
 *      1. Are we in a user/survey flow?
 *      2. Surface a nextQuestionRoute
 *         i. args:
 *            a. queryKey
 *         a. should (should we? lets think on it) handle the "last" question and go to the next survey (#5: nextSurveyRoute) set.
 *            i. if no "next" survey, go to the recommendations page.
 *      3. Surface a previousQuestionRoute
 *      4. Surface an explicit route which the user can dictate where they want to go.
 *         i. args:
 *             a. route? new survey?
 *             b. params?
 *             c. append (merge params)
 *                i. default is to override
 *      5. Surface a "nextSurveyRoute" route.
 *      6. Surface a "nextUserRouter" route.
 */

const useRouteController = () => {
  /* Store */
  const { setAnimationDirection } = useHomeHubStoreActions();

  /* Hooks */
  const router = useRouter();
  const questionDirectionFlow = useQuestionDirectionFlow();

  /* Constants */
  const [asPath] = router.asPath.split('?');
  /* Try to derive surveyType via path, if one isn't passed */
  const [, sType] = asPath.split('/').filter(Boolean);
  let derivedSurveyType: TSurveyType = SURVEY_TYPES.connectivity;

  /* our rewrites are not passing surveyType, need to get at it another way. */
  if ((SURVEY_TYPES_SANS_USER as Record<string, unknown>)[sType]) derivedSurveyType = sType as TSurveyType;

  /* ----------------- METHODS ------------------- */

  /* Move user to the next survey intended (via param or from cookie) */
  const recommendationRoute = (surveyType: string = router.query?.surveyType as string) => {
    router.push(`/surveys/recommendations/${surveyType}`);
  };

  /* Gets current question, and moves it up one */
  const nextQuestionRoute = (modifier?: number) => {
    setAnimationDirection({ direction: FLOW_ANIMATION_DIRECTION.next });
    const params = {
      ...(router.query && { question: questionDirectionFlow.nextQuestionQuery }),
    };

    if (questionDirectionFlow.isFinished) {
      recommendationRoute();
    }

    if (!questionDirectionFlow.isFinished && questionDirectionFlow.nextQuestionQuery) {
      router.push({ pathname: `${asPath}`, query: params }, undefined, { shallow: true });
    }
  };

  /* Gets current question, and moves it back one */
  const previousQuestionRoute = () => {
    setAnimationDirection({ direction: FLOW_ANIMATION_DIRECTION.previous });

    const params = {
      ...(router.query && { question: questionDirectionFlow.previousQuestionQuery }),
    };

    router.push({ pathname: `${asPath}`, query: params }, undefined, { shallow: true });
  };

  /* User passes explicit path  */
  const explicitRoute = (questionType: string) => {
    router.push({ pathname: `${asPath}`, query: { question: questionType } }, undefined, { shallow: true });
  };

  /* Move user to the wanted survey */
  const surveyRoute = (surveyType: TSurveyType = derivedSurveyType, allowPrefetch: boolean = false) => {
    if (!surveyType) {
      if (allowPrefetch) {
        return `/surveys/${router.query?.surveyType ?? derivedSurveyType}`;
      }

      return router.push(`/surveys/${router.query?.surveyType ?? derivedSurveyType}`);
    }
    if (allowPrefetch) {
      return `/surveys/${surveyType}`;
    }

    return router.push(`/surveys/${surveyType}`);
  };

  /* Moves user in /user flow (currently on need "next": shallow */
  const nextUserRoute = (step: string) => {
    router.push(`${asPath}?step=${step}`, undefined, { shallow: true });
  };

  return {
    nextQuestionRoute,
    previousQuestionRoute,
    explicitRoute,
    surveyRoute,
    nextUserRoute,
    recommendationRoute,
  };
};

export default useRouteController;
